<template>
    <DialogRoot :open="modelValue" @update:open="onUpdateOpen">
        <DialogPortal>
            <DialogOverlay class="fixed inset-0 z-50 bg-linear-to-br from-black/50 to-black/30" />

            <div class="pointer-events-none fixed inset-0 z-50 flex items-center justify-center overflow-y-auto p-4">
                <DialogContent
                    class="pointer-events-auto relative w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-2xl focus:outline-none"
                >
                    <div class="absolute inset-0 bg-linear-to-br from-gray-400/10 via-transparent to-transparent" />

                    <div class="relative flex flex-col items-center gap-6 px-8 pt-10 pb-8 text-center">
                        <div class="space-y-2">
                            <DialogTitle class="text-2xl leading-tight font-bold text-gray-900">
                                <span v-text="title" />
                            </DialogTitle>
                            <DialogDescription class="text-sm leading-relaxed text-gray-600">
                                <span v-text="description" />
                            </DialogDescription>
                        </div>

                        <div class="flex w-full flex-col-reverse gap-3 pt-2 sm:flex-row sm:justify-center">
                            <Button
                                :label="cancelLabel"
                                no-icon
                                class="border border-gray-200 bg-transparent px-6 py-3 text-gray-700 hover:border-gray-400 hover:bg-gray-50 hover:text-gray-900"
                                @click="onCancel"
                            />
                            <Button
                                :label="confirmLabel"
                                icon-size="size-4.5"
                                :leading-icon="RotateCwIcon"
                                class="bg-order-buttons px-6 py-3 text-white shadow-md"
                                @click="onConfirm"
                            />
                        </div>
                    </div>
                </DialogContent>
            </div>
        </DialogPortal>
    </DialogRoot>
</template>

<script setup lang="ts">
import Button from '@/components/buttons/Button.vue';
import { RotateCwIcon } from '@lucide/vue';
import { DialogContent, DialogDescription, DialogOverlay, DialogPortal, DialogRoot, DialogTitle } from 'reka-ui';

type StartOverConfirmModalProps = {
    modelValue: boolean;
    title: string;
    description: string;
    confirmLabel: string;
    cancelLabel: string;
};

defineProps<StartOverConfirmModalProps>();

const emits = defineEmits<{
    'update:modelValue': [value: boolean];
    confirm: [];
    cancel: [];
}>();

function close(): void {
    emits('update:modelValue', false);
}

function onConfirm(): void {
    emits('confirm');
    close();
}

function onCancel(): void {
    emits('cancel');
    close();
}

function onUpdateOpen(nextOpen: boolean): void {
    emits('update:modelValue', nextOpen);
}
</script>
